import {
    Close,
    DescriptionOutlined,
    InsertDriveFileOutlined,
    UploadFileOutlined,
} from '@mui/icons-material';
import { Box, FormControl, FormHelperText, IconButton, Stack, Typography } from '@mui/material';
import { useField, useFormikContext } from 'formik';
import { ChangeEvent, useRef } from 'react';

import useTranslate from 'hooks/useTranslate';

export interface FileInputFieldProps {
    name: string;
    label: string;
    accept?: string;
    disabled?: boolean;
    required?: boolean;
    existingUri?: string | null;
}

const FileInputField = ({
    name,
    label,
    accept,
    disabled,
    required = false,
    existingUri,
}: FileInputFieldProps) => {
    const { translate } = useTranslate();
    const [field, meta] = useField<File | null>(name);
    const { setFieldTouched, setFieldValue } = useFormikContext();
    const inputRef = useRef<HTMLInputElement | null>(null);

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.currentTarget.files?.[0] ?? null;
        setFieldValue(name, file);
        setFieldTouched(name, true, false);
    };

    const handleRemove = () => {
        setFieldValue(name, null);
        setFieldTouched(name, true, false);
        if (inputRef.current) {
            inputRef.current.value = '';
        }
    };

    const isError = Boolean(meta.touched && meta.error);
    const selectedFile = field.value;
    const hasStoredFile = Boolean(existingUri);
    const storedFileLabel = existingUri ? existingUri.split('/').pop() || existingUri : '';
    const displayLabel = selectedFile?.name || storedFileLabel || translate('Upload your file');
    const helperText = selectedFile
        ? `${(selectedFile.size / 1024 / 1024).toFixed(2)} MB`
        : hasStoredFile
          ? translate('A file is already stored on the server')
          : translate('PDF, DOC, or DOCX');

    return (
        <FormControl error={isError} fullWidth>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
                {required ? `${translate(label)} *` : translate(label)}
            </Typography>
            <Box
                component="label"
                sx={{
                    position: 'relative',
                    border: '1px dashed',
                    borderColor: isError ? 'error.main' : selectedFile ? 'success.main' : 'divider',
                    borderRadius: 3,
                    p: 2,
                    background: selectedFile
                        ? 'linear-gradient(180deg, rgba(76, 175, 80, 0.08) 0%, rgba(76, 175, 80, 0.02) 100%)'
                        : 'linear-gradient(180deg, rgba(25, 118, 210, 0.05) 0%, rgba(25, 118, 210, 0.015) 100%)',
                    transition:
                        'border-color 0.2s ease, background 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.7 : 1,
                    '&:hover': disabled
                        ? undefined
                        : { borderColor: selectedFile ? 'success.dark' : 'primary.main' },
                }}
            >
                <input
                    ref={inputRef}
                    hidden
                    type="file"
                    accept={accept}
                    onChange={handleChange}
                    disabled={disabled}
                />
                <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: selectedFile
                                    ? 'rgba(76, 175, 80, 0.18)'
                                    : 'rgba(25, 118, 210, 0.12)',
                                color: selectedFile ? 'success.dark' : 'primary.main',
                                flexShrink: 0,
                            }}
                        >
                            {selectedFile ? <DescriptionOutlined /> : <UploadFileOutlined />}
                        </Box>

                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700 }}>
                                {displayLabel}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                {selectedFile
                                    ? helperText
                                    : hasStoredFile
                                      ? translate(
                                            'Click to replace the stored file or preview it first'
                                        )
                                      : translate(
                                            'Click anywhere in this area to choose a document'
                                        )}
                            </Typography>
                        </Box>

                        {selectedFile ? (
                            <IconButton
                                size="small"
                                disabled={disabled}
                                onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    handleRemove();
                                }}
                                sx={{
                                    mt: -0.5,
                                    mr: -0.5,
                                    color: 'text.secondary',
                                    '&:hover': {
                                        color: 'error.main',
                                        backgroundColor: 'rgba(211, 47, 47, 0.08)',
                                    },
                                }}
                            >
                                <Close fontSize="small" />
                            </IconButton>
                        ) : null}
                    </Stack>

                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: 1.25,
                            py: 1,
                            borderRadius: 2,
                            backgroundColor: 'background.paper',
                            border: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{ alignItems: 'center', minWidth: 0 }}
                        >
                            <InsertDriveFileOutlined
                                sx={{ color: 'text.secondary', fontSize: 18 }}
                            />
                            <Typography variant="caption" color="text.secondary" noWrap>
                                {selectedFile
                                    ? translate('Tap to replace this file')
                                    : hasStoredFile
                                      ? translate('Tap to replace this file')
                                      : `${translate('Supported formats')}: ${helperText}`}
                            </Typography>
                        </Stack>
                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 700,
                                color: selectedFile ? 'success.dark' : 'primary.main',
                                flexShrink: 0,
                            }}
                        >
                            {translate(selectedFile || hasStoredFile ? 'replace' : 'browse')}
                        </Typography>
                    </Stack>
                </Stack>
            </Box>
            <FormHelperText>{isError ? meta.error : ' '}</FormHelperText>
        </FormControl>
    );
};

export default FileInputField;
